import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { useTheme, alpha } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link } from 'react-router-dom';

import {
  getTierName,
  featureConfigs,
  FeatureId,
  getFeaturesForTier,
} from '../../config/featureConfig';
import { useLicenseEntitlement } from '../../hooks/useLicenseEntitlement';
import { SubscriptionTier } from '../../models/Subscription';

/**
 * Props for license denial data from API 403 response
 */
export interface LicenseDenialData {
  code: string;
  message: string;
  requiredTier?: SubscriptionTier;
  featureId?: FeatureId;
  featureName?: string;
  seats?: {
    entitled: number;
    used: number;
  };
  gracePeriodEndsUtc?: string;
}

export interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  denialData?: LicenseDenialData;
  featureId?: FeatureId;
  requiredTier?: SubscriptionTier;
  onRemindLater?: () => void;
  /** HA-150: Callback when upgrade/view plans button is clicked */
  onUpgradeClick?: () => void;
}

/**
 * UpgradeModal Component (HA-109)
 * Displays when a 403 license denial is received from the API
 * Shows current vs required tier comparison with upgrade CTA
 */
const UpgradeModal: React.FC<UpgradeModalProps> = ({
  open,
  onClose,
  denialData,
  featureId,
  requiredTier,
  onRemindLater,
  onUpgradeClick,
}) => {
  const theme = useTheme();
  const { tier, tierName } = useLicenseEntitlement();

  // Determine the required tier from props or denial data
  const targetTier = requiredTier ?? denialData?.requiredTier ?? SubscriptionTier.Basic;
  const targetTierName = getTierName(targetTier);

  // Get feature information
  const feature = featureId ? featureConfigs[featureId] : null;
  const featureName = denialData?.featureName ?? feature?.name ?? 'this feature';
  const featureDescription = feature?.description ?? denialData?.message ?? '';

  // Get features available at target tier that user doesn't have
  const targetTierFeatures = getFeaturesForTier(targetTier)
    .filter((f) => f.requiredTier > tier)
    .slice(0, 5);

  const handleRemindLater = () => {
    if (onRemindLater) {
      onRemindLater();
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        },
      }}
    >
      {/* Header with gradient */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          p: 3,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              backgroundColor: alpha('#fff', 0.2),
              borderRadius: '50%',
              p: 1.5,
              display: 'flex',
            }}
          >
            <LockIcon fontSize="large" />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
              }}
            >
              Upgrade Required
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Unlock {featureName}
            </Typography>
          </Box>
        </Box>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        {/* Feature description */}
        {featureDescription && (
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 3,
            }}
          >
            {featureDescription}
          </Typography>
        )}

        {/* Tier comparison */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            mb: 3,
            p: 2,
            backgroundColor: alpha(theme.palette.grey[100], 0.5),
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 80,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              Your Plan
            </Typography>
            <Chip label={tierName} size="medium" sx={{ fontWeight: 600 }} />
          </Box>

          <ArrowForwardIcon color="primary" sx={{ mx: 1 }} />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 80,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              Required
            </Typography>
            <Chip label={targetTierName} size="medium" color="primary" sx={{ fontWeight: 600 }} />
          </Box>
        </Box>

        {/* Features you'll unlock */}
        {targetTierFeatures.length > 0 && (
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Features you&apos;ll unlock with {targetTierName}:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
              {targetTierFeatures.map((f) => (
                <Box
                  component="li"
                  key={f.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <CheckCircleIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
                  <Typography variant="body2">{f.name}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Seat usage warning if applicable */}
        {denialData?.seats && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                <strong>Usage:</strong> {denialData.seats.used} of {denialData.seats.entitled} seats
                used
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          p: 2.5,
          pt: 2,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          variant="text"
          size="small"
          onClick={handleRemindLater}
          sx={{
            mr: 'auto',
            color: 'text.secondary',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          Remind me later
        </Button>
        <Button variant="text" color="inherit" onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          component={Link}
          to="/subscription"
          variant="contained"
          color="primary"
          startIcon={<UpgradeIcon />}
          sx={{ textTransform: 'none', px: 3 }}
          onClick={() => {
            onUpgradeClick?.();
            onClose();
          }}
        >
          View Plans
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradeModal;
